import { IconRefresh } from "@tabler/icons-react";
import { useDocumentosSolicitud } from "@/features/documentos/hooks/use-documentos-solicitud";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useCargarCfdi } from "@/features/cargar-cfdi/hooks/use-cargar-cfdi";
import { useSatValidationFlow } from "@/features/cargar-cfdi/hooks/use-sat-validation-flow";
import type { SatError } from "@/features/cargar-cfdi/types";
import { validarParejaCfdi } from "@/features/cargar-cfdi/utils/validar-pareja";
import { useEsDueno } from "@/features/solicitudes/hooks/use-es-dueno";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { Button, Group, PageHeader, ReadOnlyBanner, Stack } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { estaPostEnvio } from "@/lib/estado-solicitud";
import { toastAdvertencia } from "@/lib/toast";
import { qk } from "@/lib/query-keys";
import type { CargarCFDIResultado } from "@/lib/types";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/cargar-cfdi";
import { CFDICargadoCard } from "./CFDICargadoCard";
import { CFDIPdfDropzone } from "./CFDIPdfDropzone";
import { ReglasRechazadasCard } from "./ReglasRechazadasCard";
import { SatErrorCard } from "./SatErrorCard";
import { SatValidacionProgreso } from "./SatValidacionProgreso";
import { VentanasPuePpdBanner } from "./VentanasPuePpdBanner";

/**
 * Container · maneja el flujo dual XML+PDF:
 *
 *   1. El usuario elige XML + PDF (pareja con mismo nombre base).
 *   2. Validación local (extensión, header PDF, XML CFDI 4.0, nombres
 *      coinciden) en `CFDIPdfDropzone`.
 *   3. Al pulsar "Validar CFDI" se suben XML + PDF juntos
 *      (POST /api/solicitudes/{id}/cfdi). Esto dispara reglas RN1-RN12 y, si
 *      pasan, el servidor registra ambos archivos en el expediente
 *      (Gestión Documental · D-2).
 *   4. Si reglas pasan, polling SAT.
 *   5. Cuando SAT marca Validado, navega a /datos-generales.
 */
export function CargarCFDIPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: sol } = useSolicitud(id);
  // El XML y el PDF del CFDI los registra Gestión Documental por el outbox unos segundos después
  // de pasar las reglas · mientras el CFDI ya pasó las reglas y aún no llegan, se consulta la lista
  // cada 2 s (se apaga sola en cuanto aparecen; con las reglas rechazadas nunca se registran).
  const esperaFacturaEnExpediente = !!sol.cfdi && sol.estado !== "CfdiCargado" && sol.estado !== "ReglasRechazadas";
  const { data: documentos = [] } = useDocumentosSolicitud(id, {
    refetchInterval: (lista) =>
      esperaFacturaEnExpediente && !(lista ?? []).some((d) => d.categoria === "FacturaXml") ? 2_000 : false,
  });

  const [resultado, setResultado] = useState<CargarCFDIResultado | null>(null);
  const [errorSat, setErrorSat] = useState<SatError | null>(null);
  // Input múltiple oculto para el botón "Reemplazar CFDI" (abre el diálogo
  // directo · sin mostrar el dropzone).
  const reemplazarInputRef = useRef<HTMLInputElement>(null);

  const sat = useSatValidationFlow(id, {
    onValidado: () => {
      // SAT vigente · el XML y el PDF ya los registró el servidor en el expediente.
      void qc.invalidateQueries({ queryKey: qk.solicitud(id) });
      void qc.invalidateQueries({ queryKey: qk.documentosSolicitud(id) });
      navigate({ to: "/solicitudes/$id/datos-generales", params: { id }, replace: true });
    },
    onErrorSat: (err) => {
      setErrorSat(err);
    },
  });

  const upload = useCargarCfdi(id, {
    onReglasAutorizadas: (r) => {
      setResultado(r);
      setErrorSat(null);
      sat.start();
    },
    onReglasRechazadas: (r) => {
      setResultado(r);
      setErrorSat(null);
    },
  });

  const esDueno = useEsDueno(sol);
  // Post-envío, o viewer que no es el dueño (Mesa de Control/Auditor/Admin
  // revisando una solicitud ajena · P29) · en ambos casos sin mutaciones.
  const soloLectura = estaPostEnvio(sol.estado) || !esDueno;
  const cfdiCargado = sol.cfdi ?? null;
  // Cadena de subida en curso · upload XML+PDF y polling SAT. Se usa para
  // mostrar el progreso y ocultar acciones.
  const procesoEnCurso = upload.isPending || sat.esperando;

  // Dispara la cadena de validación (reglas → SAT → subida de soporte) con la
  // pareja XML+PDF · compartido por el dropzone (carga inicial) y el botón
  // "Reemplazar CFDI".
  const iniciarValidacion = (xml: File, pdf: File) => {
    setResultado(null);
    setErrorSat(null);
    upload.mutate({ xml, pdf });
  };

  // Handler del input múltiple del botón "Reemplazar CFDI": reparte por
  // extensión, valida la pareja en cliente y, si pasa, arranca la validación.
  const onArchivosReemplazo = async (files: File[]) => {
    const xml = files.find((f) => f.name.toLowerCase().endsWith(".xml")) ?? null;
    const pdf = files.find((f) => f.name.toLowerCase().endsWith(".pdf")) ?? null;
    const v = await validarParejaCfdi(xml, pdf);
    if (!v.ok || !xml || !pdf) {
      toastAdvertencia({
        title: "Archivos inválidos",
        message: v.errorXml || v.errorPdf || v.errorPareja || "Selecciona el XML y el PDF del CFDI (mismo nombre, sólo cambia la extensión).",
      });
      return;
    }
    iniciarValidacion(xml, pdf);
  };

  return (
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title="Cargar CFDI"
        description={`Solicitud ${id.slice(0, 8)}… · subir XML + PDF del CFDI 4.0`}
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: id.slice(0, 8), href: `/solicitudes/${id}` },
          { label: "Cargar CFDI" },
        ]}
      />
      <LayoutConStepper estado={sol.estado} subtipo={sol.subtipo} tipo={sol.tipo} prorrateoAutomatico={sol.prorrateoAutomatico} paso="cfdi" solicitudId={id} contabilizada={sol.enviadaAContabilizacion} fillHeight>
        <Stack gap="md">
          {soloLectura && <ReadOnlyBanner motivo={esDueno ? "post-envio" : "no-dueno"} mensaje="El CFDI cargado no puede reemplazarse." />}

          {/* Ventanas de recepción PUE/PPD vigentes (P41 · D-1) · el usuario ve
              las fechas límite ANTES de cargar (solo cuando puede cargar). */}
          {!soloLectura && <VentanasPuePpdBanner solicitudId={id} />}

          {/* Si ya hay un CFDI cargado, siempre mostramos su detalle + el
              resultado de la validación SAT (el usuario puede previsualizar los
              archivos desde ahí). */}
          {cfdiCargado && <CFDICargadoCard cfdi={cfdiCargado} solicitudId={id} documentos={documentos} />}

          {/* Carga inicial (aún sin CFDI): dropzone completo con selección
              conjunta o arrastre. */}
          {!soloLectura && !cfdiCargado && (
            <CFDIPdfDropzone cargando={upload.isPending} esperandoSat={sat.esperando} onSubmit={iniciarValidacion} />
          )}

          {/* Con un CFDI ya cargado, el reemplazo es un solo botón que abre el
              diálogo de selección (XML + PDF) y valida automáticamente · sin
              repetir el dropzone (el detalle + preview ya cubren la consulta). */}
          {!soloLectura && cfdiCargado && !procesoEnCurso && (
            <Group>
              <Button variant="default" leftSection={<IconRefresh size={14} />} onClick={() => reemplazarInputRef.current?.click()}>
                Reemplazar CFDI
              </Button>
            </Group>
          )}

          {/* Oculto visualmente pero renderizado (no display:none) · algunos
              navegadores no abren el diálogo nativo al hacer .click() sobre un
              input display:none. */}
          <input
            ref={reemplazarInputRef}
            type="file"
            accept=".xml,application/xml,text/xml,.pdf,application/pdf"
            multiple
            onChange={(e) => {
              void onArchivosReemplazo(Array.from(e.currentTarget.files ?? []));
              e.currentTarget.value = "";
            }}
            className="absolute h-px w-px overflow-hidden opacity-0"
            style={{ pointerEvents: "none" }}
            tabIndex={-1}
            aria-hidden="true"
          />

          {procesoEnCurso && <SatValidacionProgreso />}

          {resultado && !resultado.reglasAutorizadas && <ReglasRechazadasCard errores={resultado.erroresReglas} solicitudId={id} numeroSolicitud={sol.numeroSolicitud} />}
          {errorSat && <SatErrorCard error={errorSat} />}
        </Stack>
      </LayoutConStepper>
    </div>
  );
}
